﻿package org.zq.pixelset.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CloudUpload
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Security
import androidx.compose.material.icons.filled.SwapHoriz
import androidx.compose.material.icons.outlined.AspectRatio
import androidx.compose.material.icons.outlined.Crop
import androidx.compose.material.icons.outlined.RotateRight
import androidx.compose.material.icons.outlined.Tune
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.zq.pixelset.PixelSetViewModel
import org.zq.pixelset.model.ImageItem
import org.zq.pixelset.model.OutputFormat
import org.zq.pixelset.model.ProcessingMode
import org.zq.pixelset.model.RotationStep
import org.zq.pixelset.model.ScaleMode
import org.zq.pixelset.model.SizePreset
import org.zq.pixelset.model.formatFileSize
import org.zq.pixelset.model.sizeReductionPercent
import org.zq.pixelset.platform.openExternalUrl
import org.zq.pixelset.platform.onImageFileDrop
import org.zq.pixelset.theme.PixelSetColors
import org.zq.pixelset.theme.PixelSetTheme
import org.zq.pixelset.theme.WindowSizeClass
import org.zq.pixelset.theme.comparisonHeight
import org.zq.pixelset.theme.fileListMaxHeight
import org.zq.pixelset.theme.horizontalPadding
import org.zq.pixelset.theme.isCompact
import org.zq.pixelset.theme.sidebarWidth
import org.zq.pixelset.theme.uploadZoneHeight
import org.zq.pixelset.theme.windowSizeClass

@Composable
fun PixelSetApp() {
    PixelSetTheme {
        val scope = rememberCoroutineScope()
        val viewModel = remember { PixelSetViewModel(scope) }
        PixelSetScreen(viewModel)
    }
}

@Composable
fun PixelSetScreen(viewModel: PixelSetViewModel) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        val sizeClass = windowSizeClass(maxWidth)
        val contentPadding = sizeClass.horizontalPadding()

        Box(Modifier.fillMaxSize()) {
            Scaffold(
                containerColor = PixelSetColors.Background,
                topBar = { PixelSetHeader(sizeClass) },
                bottomBar = { PixelSetFooter(sizeClass) },
            ) { padding ->
                if (sizeClass.isCompact()) {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(padding)
                            .verticalScroll(rememberScrollState())
                            .padding(horizontal = contentPadding, vertical = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        Sidebar(viewModel = viewModel, sizeClass = sizeClass)
                        MainContent(
                            viewModel = viewModel,
                            sizeClass = sizeClass,
                            scrollable = false,
                        )
                    }
                } else {
                    Row(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(padding)
                            .padding(horizontal = contentPadding, vertical = 16.dp),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                    ) {
                        Sidebar(
                            viewModel = viewModel,
                            sizeClass = sizeClass,
                            modifier = Modifier
                                .width(sizeClass.sidebarWidth())
                                .fillMaxHeight(),
                        )
                        MainContent(
                            viewModel = viewModel,
                            sizeClass = sizeClass,
                            modifier = Modifier.weight(1f).fillMaxHeight(),
                            scrollable = true,
                        )
                    }
                }
            }
            viewModel.errorMessage?.let { msg ->
                Box(
                    Modifier
                        .fillMaxSize()
                        .padding(bottom = if (sizeClass.isCompact()) 8.dp else 16.dp),
                    contentAlignment = Alignment.BottomCenter,
                ) {
                    PixelSetText(
                        text = msg,
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier
                            .padding(horizontal = contentPadding)
                            .fillMaxWidth()
                            .widthIn(max = 480.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(MaterialTheme.colorScheme.errorContainer)
                            .padding(12.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun PixelSetHeader(sizeClass: WindowSizeClass) {
    val padding = sizeClass.horizontalPadding()
    Column {
        if (sizeClass.isCompact()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(PixelSetColors.Surface)
                    .padding(horizontal = padding, vertical = 12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                PixelSetText(
                    text = "PixelSet",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    color = PixelSetColors.Primary,
                )
                LocalProcessBadge()
            }
        } else {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(PixelSetColors.Surface)
                    .padding(horizontal = padding, vertical = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PixelSetText(
                    text = "PixelSet",
                    fontSize = 22.sp,
                    fontWeight = FontWeight.Bold,
                    color = PixelSetColors.Primary,
                )
                LocalProcessBadge()
            }
        }
        HorizontalDivider(color = PixelSetColors.Border)
    }
}

@Composable
private fun LocalProcessBadge() {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(PixelSetColors.PrimaryLight)
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Icon(Icons.Default.Security, null, tint = PixelSetColors.Primary, modifier = Modifier.size(16.dp))
        PixelSetText("本地处理", fontSize = 13.sp, color = PixelSetColors.Primary, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun Sidebar(
    viewModel: PixelSetViewModel,
    sizeClass: WindowSizeClass,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.then(
            if (sizeClass.isCompact()) Modifier.fillMaxWidth() else Modifier.fillMaxHeight(),
        ),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        UploadZone(
            onClick = { viewModel.pickAndLoadImages() },
            onFilesDropped = { viewModel.onFilesDropped(it) },
            onDropFailed = { viewModel.onDropFailed() },
            isLoading = viewModel.isProcessing && !viewModel.hasImages,
            height = sizeClass.uploadZoneHeight(),
        )
        if (viewModel.images.isNotEmpty()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PixelSetText(
                    "${viewModel.images.size} 张图片",
                    fontSize = 12.sp,
                    color = PixelSetColors.TextSecondary,
                    fontWeight = FontWeight.Medium,
                )
                PixelSetText(
                    "清空",
                    fontSize = 12.sp,
                    color = PixelSetColors.Primary,
                    modifier = Modifier.clickable { viewModel.clearAllImages() },
                )
            }
            val listModifier = if (sizeClass.isCompact()) {
                Modifier.heightIn(max = sizeClass.fileListMaxHeight())
            } else {
                Modifier.weight(1f, fill = true)
            }
            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = listModifier,
            ) {
                items(viewModel.images, key = { it.id }) { item ->
                    FileCard(
                        item = item,
                        isSelected = item.id == viewModel.selectedId,
                        onClick = { viewModel.selectImage(item.id) },
                        onRemove = { viewModel.removeImage(item.id) },
                    )
                }
            }
        }
    }
}

@Composable
private fun UploadZone(
    onClick: () -> Unit,
    onFilesDropped: (List<org.zq.pixelset.model.PickedImage>) -> Unit,
    onDropFailed: () -> Unit,
    isLoading: Boolean,
    height: androidx.compose.ui.unit.Dp = 160.dp,
) {
    var isDragActive by remember { mutableStateOf(false) }
    val borderColor = if (isDragActive) PixelSetColors.Primary else PixelSetColors.BorderDashed
    val backgroundColor = if (isDragActive) PixelSetColors.PrimaryContainer else PixelSetColors.Surface

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .clip(RoundedCornerShape(12.dp))
            .background(backgroundColor)
            .border(
                width = if (isDragActive) 2.dp else 2.dp,
                color = borderColor,
                shape = RoundedCornerShape(12.dp),
            )
            .clickable(onClick = onClick)
            .onImageFileDrop(
                onDragActiveChange = { isDragActive = it },
                onFilesDropped = onFilesDropped,
                onDropFailed = onDropFailed,
            ),
        contentAlignment = Alignment.Center,
    ) {
        if (isLoading) {
            CircularProgressIndicator(color = PixelSetColors.Primary, modifier = Modifier.size(32.dp))
        } else {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(
                    Icons.Default.CloudUpload,
                    null,
                    tint = if (isDragActive) PixelSetColors.Primary else PixelSetColors.TextMuted,
                    modifier = Modifier.size(36.dp),
                )
                PixelSetText(
                    if (isDragActive) "松开鼠标即可上传" else "拖拽图片至此，或点击上传",
                    fontSize = 14.sp,
                    color = if (isDragActive) PixelSetColors.Primary else PixelSetColors.TextSecondary,
                    fontWeight = if (isDragActive) FontWeight.Medium else FontWeight.Normal,
                )
                PixelSetText("JPG / PNG / WebP", fontSize = 12.sp, color = PixelSetColors.TextMuted)
            }
        }
    }
}

@Composable
private fun FileCard(
    item: ImageItem,
    isSelected: Boolean,
    onClick: () -> Unit,
    onRemove: () -> Unit,
) {
    val borderColor = if (isSelected) PixelSetColors.Primary else PixelSetColors.Border
    val backgroundColor = if (isSelected) PixelSetColors.PrimaryContainer else PixelSetColors.Surface
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(backgroundColor)
            .border(1.dp, borderColor, RoundedCornerShape(10.dp))
            .clickable(onClick = onClick)
            .padding(10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Image(
            bitmap = item.thumbnail,
            contentDescription = item.picked.name,
            contentScale = ContentScale.Crop,
            modifier = Modifier
                .size(44.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(PixelSetColors.Border),
        )
        Column(modifier = Modifier.weight(1f)) {
            PixelSetText(item.picked.name, fontSize = 13.sp, fontWeight = FontWeight.Medium, maxLines = 1)
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                PixelSetText(formatFileSize(item.picked.sizeBytes), fontSize = 12.sp, color = PixelSetColors.TextMuted)
                if (item.processedResult != null) {
                    Badge("已处理", PixelSetColors.PrimaryLight, PixelSetColors.Primary)
                } else if (item.isProcessing) {
                    PixelSetText("处理中…", fontSize = 11.sp, color = PixelSetColors.Primary)
                } else if (item.lastProcessError != null) {
                    Badge("失败", MaterialTheme.colorScheme.errorContainer, MaterialTheme.colorScheme.error)
                }
            }
        }
        IconButton(onClick = onRemove, modifier = Modifier.size(32.dp)) {
            Icon(Icons.Default.Delete, "删除", tint = PixelSetColors.TextMuted, modifier = Modifier.size(18.dp))
        }
    }
}

@Composable
private fun MainContent(
    viewModel: PixelSetViewModel,
    sizeClass: WindowSizeClass,
    modifier: Modifier = Modifier,
    scrollable: Boolean = true,
) {
    val columnModifier = modifier.then(
        if (scrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier,
    )
    Column(
        modifier = columnModifier,
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        LoadWarningBanner(viewModel.loadWarning)
        ComparisonView(viewModel, sizeClass)
        if (!viewModel.isConvertMode) {
            DimensionInfo(viewModel, sizeClass)
        }
        BatchFailuresPanel(viewModel)
        SettingsPanel(viewModel, sizeClass)
        ActionButtons(viewModel, sizeClass)
    }
}

@Composable
private fun LoadWarningBanner(warning: String?) {
    if (warning.isNullOrBlank()) return
    PixelSetText(
        text = warning,
        fontSize = 12.sp,
        color = PixelSetColors.Primary,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(PixelSetColors.PrimaryContainer)
            .padding(12.dp),
    )
}

@Composable
private fun BatchFailuresPanel(viewModel: PixelSetViewModel) {
    if (viewModel.batchFailures.isEmpty()) return
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(MaterialTheme.colorScheme.errorContainer)
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            PixelSetText(
                "批量处理失败 (${viewModel.batchFailures.size})",
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onErrorContainer,
            )
            PixelSetText(
                "清除",
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.clickable { viewModel.clearBatchFailures() },
            )
        }
        viewModel.batchFailures.take(5).forEach { failure ->
            PixelSetText(
                "• ${failure.imageName}: ${failure.message}",
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.onErrorContainer,
            )
        }
        if (viewModel.batchFailures.size > 5) {
            PixelSetText(
                "… 另有 ${viewModel.batchFailures.size - 5} 条",
                fontSize = 12.sp,
                color = MaterialTheme.colorScheme.onErrorContainer,
            )
        }
    }
}

@Composable
private fun ComparisonView(viewModel: PixelSetViewModel, sizeClass: WindowSizeClass) {
    val original = viewModel.originalBitmap
    val processed = viewModel.processedBitmap
    val isPreviewing = viewModel.selectedItem?.isProcessing == true
    val imageW = viewModel.originalWidth
    val imageH = viewModel.originalHeight

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(sizeClass.comparisonHeight())
            .clip(RoundedCornerShape(12.dp))
            .background(PixelSetColors.SurfaceVariant)
            .border(1.dp, PixelSetColors.Border, RoundedCornerShape(12.dp))
            .clipToBounds(),
    ) {
        if (original == null) {
            PlaceholderBox("上传图片后可预览对比效果", Modifier.fillMaxSize())
        } else {
            BoxWithConstraints(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center,
            ) {
                val containerW = maxWidth
                val containerH = maxHeight
                val fraction = viewModel.comparisonPosition.coerceIn(0.05f, 0.95f)
                val clipWidth = containerW * fraction

                Box(
                    modifier = Modifier
                        .size(containerW, containerH)
                        .pointerInput(containerW) {
                            detectDragGestures { change, drag ->
                                change.consume()
                                val w = size.width.toFloat().coerceAtLeast(1f)
                                viewModel.comparisonPosition =
                                    ((change.position.x + drag.x) / w).coerceIn(0.05f, 0.95f)
                            }
                        },
                ) {
                    if (processed != null) {
                        Image(
                            bitmap = processed,
                            contentDescription = "处理后",
                            contentScale = ContentScale.Fit,
                            alignment = Alignment.Center,
                            modifier = Modifier.fillMaxSize(),
                        )
                        Box(
                            Modifier
                                .width(clipWidth)
                                .fillMaxHeight()
                                .align(Alignment.CenterStart)
                                .clipToBounds(),
                        ) {
                            Box(Modifier.size(containerW, containerH)) {
                                Image(
                                    bitmap = original,
                                    contentDescription = "原图",
                                    contentScale = ContentScale.Fit,
                                    alignment = Alignment.Center,
                                    modifier = Modifier.fillMaxSize(),
                                )
                                if (!viewModel.isConvertMode && viewModel.cropEditorEnabled && imageW > 0 && imageH > 0) {
                                    CropOverlay(
                                        imageWidth = imageW,
                                        imageHeight = imageH,
                                        cropRect = viewModel.visualCrop,
                                        onCropChange = viewModel::updateVisualCrop,
                                        modifier = Modifier.fillMaxSize(),
                                    )
                                }
                            }
                        }
                        Box(
                            modifier = Modifier
                                .fillMaxHeight()
                                .width(2.dp)
                                .offset(x = clipWidth - 1.dp)
                                .align(Alignment.CenterStart)
                                .background(PixelSetColors.Primary),
                        )
                        Box(
                            modifier = Modifier
                                .offset(x = clipWidth - 16.dp, y = containerH / 2 - 16.dp)
                                .align(Alignment.CenterStart)
                                .size(32.dp)
                                .clip(CircleShape)
                                .background(PixelSetColors.Primary),
                            contentAlignment = Alignment.Center,
                        ) {
                            Icon(Icons.Default.SwapHoriz, null, tint = Color.White, modifier = Modifier.size(18.dp))
                        }
                        Box(Modifier.align(Alignment.TopStart).padding(8.dp)) {
                            LabelBadge("原图", PixelSetColors.PrimaryLight.copy(alpha = 0.92f), PixelSetColors.Primary)
                        }
                        Box(Modifier.align(Alignment.TopEnd).padding(8.dp)) {
                            LabelBadge("处理后", PixelSetColors.Primary, Color.White)
                        }
                    } else {
                        Image(
                            bitmap = original,
                            contentDescription = "原图",
                            contentScale = ContentScale.Fit,
                            alignment = Alignment.Center,
                            modifier = Modifier.fillMaxSize(),
                        )
                        if (!viewModel.isConvertMode && viewModel.cropEditorEnabled && imageW > 0 && imageH > 0) {
                            CropOverlay(
                                imageWidth = imageW,
                                imageHeight = imageH,
                                cropRect = viewModel.visualCrop,
                                onCropChange = viewModel::updateVisualCrop,
                                modifier = Modifier.fillMaxSize(),
                            )
                        }
                    }
                }
            }
            if (isPreviewing) {
                Box(
                    Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.25f)),
                    contentAlignment = Alignment.Center,
                ) {
                    CircularProgressIndicator(color = PixelSetColors.Primary)
                }
            }
        }
    }
}

@Composable
private fun LabelBadge(text: String, bg: Color, fg: Color) {
    PixelSetText(
        text = text,
        modifier = Modifier
            .clip(RoundedCornerShape(6.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
        fontSize = 12.sp,
        fontWeight = FontWeight.Medium,
        color = fg,
    )
}

@Composable
private fun DimensionInfo(viewModel: PixelSetViewModel, sizeClass: WindowSizeClass) {
    if (viewModel.originalWidth == 0) return
    val processedW = viewModel.processedResult?.width ?: viewModel.targetWidth
    val processedH = viewModel.processedResult?.height ?: viewModel.targetHeight
    val reduction = viewModel.currentFile?.let { file ->
        viewModel.processedResult?.let { result ->
            sizeReductionPercent(file.sizeBytes, result.bytes.size.toLong())
        }
    }

    val originalLine = @Composable {
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Outlined.AspectRatio, null, tint = PixelSetColors.TextMuted, modifier = Modifier.size(16.dp))
            PixelSetText(
                "${viewModel.originalWidth}×${viewModel.originalHeight}",
                fontSize = 13.sp,
                color = PixelSetColors.TextSecondary,
            )
        }
    }
    val processedLine = @Composable {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            Icon(Icons.Outlined.AspectRatio, null, tint = PixelSetColors.TextMuted, modifier = Modifier.size(16.dp))
            PixelSetText("$processedW×$processedH", fontSize = 13.sp, color = PixelSetColors.TextSecondary)
            reduction?.let { pct ->
                val label = if (pct >= 0) "-$pct%" else "+${-pct}%"
                val bg = if (pct >= 0) PixelSetColors.PrimaryLight else PixelSetColors.SurfaceVariant
                val fg = if (pct >= 0) PixelSetColors.Primary else PixelSetColors.TextSecondary
                Badge(label, bg, fg)
            }
        }
    }

    if (sizeClass.isCompact()) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            originalLine()
            processedLine()
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            originalLine()
            processedLine()
        }
    }
}

@Composable
private fun SettingsPanel(viewModel: PixelSetViewModel, sizeClass: WindowSizeClass) {
    val panelPadding = if (sizeClass.isCompact()) 16.dp else 20.dp
    val pixelSection = @Composable {
        Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            if (!viewModel.isConvertMode) {
                SectionTitle({ Icon(Icons.Outlined.Crop, null, tint = PixelSetColors.Primary, modifier = Modifier.size(18.dp)) }, "像素设置")
                PixelSetText(
                    "当前图片独立参数 · ${viewModel.currentFile?.name ?: ""}",
                    fontSize = 11.sp,
                    color = PixelSetColors.TextMuted,
                    maxLines = 1,
                )
            }
            PixelSetText("处理模式", fontSize = 12.sp, color = PixelSetColors.TextMuted)
            SegmentedControl(
                options = ProcessingMode.entries.map { it.label },
                selectedIndex = viewModel.processingModeIndex,
                onSelected = viewModel::updateProcessingMode,
            )
            if (viewModel.isConvertMode) {
                PixelSetText(
                    "仅转换图片格式（PNG↔JPG↔WebP），不改变尺寸与内容",
                    fontSize = 12.sp,
                    color = PixelSetColors.Primary,
                )
            } else if (viewModel.isCompressMode) {
                PixelSetText(
                    "仅压缩：保持裁剪/旋转后尺寸，只调整格式与质量",
                    fontSize = 12.sp,
                    color = PixelSetColors.Primary,
                )
            } else {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    DimensionInput("W", viewModel.targetWidth, viewModel::onWidthChange, Modifier.weight(1f))
                    AspectRatioLockButton(viewModel.lockAspectRatio, viewModel::toggleLockAspectRatio)
                    DimensionInput("H", viewModel.targetHeight, viewModel::onHeightChange, Modifier.weight(1f))
                }
                PixelDropdown(
                    label = "预设尺寸",
                    options = SizePreset.presets.map { it.label },
                    selectedIndex = viewModel.selectedPresetIndex,
                    onSelected = viewModel::onPresetSelected,
                )
            }
        }
    }
    val outputSection = @Composable {
        Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            SectionTitle({ Icon(Icons.Outlined.Tune, null, tint = PixelSetColors.Primary, modifier = Modifier.size(18.dp)) }, "输出设置")
            PixelSetText("格式", fontSize = 12.sp, color = PixelSetColors.TextMuted)
            SegmentedControl(
                options = OutputFormat.entries.map { it.label },
                selectedIndex = OutputFormat.entries.indexOf(viewModel.outputFormat),
                onSelected = { viewModel.updateOutputFormat(OutputFormat.entries[it]) },
            )
            if (viewModel.isConvertMode && viewModel.outputFormat == OutputFormat.JPG) {
                QualitySlider(viewModel.quality, viewModel::updateQuality)
            } else if (!viewModel.isConvertMode) {
                QualitySlider(viewModel.quality, viewModel::updateQuality)
            }
            if (!viewModel.isConvertMode && !viewModel.isCompressMode) {
                PixelSetText("缩放模式", fontSize = 12.sp, color = PixelSetColors.TextMuted)
                SegmentedControl(
                    options = ScaleMode.entries.map { it.label },
                    selectedIndex = ScaleMode.entries.indexOf(viewModel.scaleMode),
                    onSelected = { viewModel.updateScaleMode(ScaleMode.entries[it]) },
                )
            }
        }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(PixelSetColors.Surface)
            .border(1.dp, PixelSetColors.Border, RoundedCornerShape(12.dp))
            .padding(panelPadding),
        verticalArrangement = Arrangement.spacedBy(if (sizeClass.isCompact()) 20.dp else 16.dp),
    ) {
        if (!viewModel.isConvertMode) {
            AutoPreviewToggle(
                enabled = viewModel.autoPreviewEnabled,
                onEnabledChange = viewModel::onAutoPreviewChanged,
            )
        }
        if (viewModel.isConvertMode) {
            pixelSection()
            HorizontalDivider(color = PixelSetColors.Border)
            outputSection()
        } else if (sizeClass.isCompact()) {
            pixelSection()
            HorizontalDivider(color = PixelSetColors.Border)
            outputSection()
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                Box(Modifier.weight(1f)) { pixelSection() }
                Box(Modifier.weight(1f)) { outputSection() }
            }
        }
    }
}

@Composable
private fun AdvancedEditSection(viewModel: PixelSetViewModel) {
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SectionTitle(
                { Icon(Icons.Outlined.RotateRight, null, tint = PixelSetColors.Primary, modifier = Modifier.size(18.dp)) },
                "高级编辑",
            )
            PixelSetText(
                "重置",
                fontSize = 12.sp,
                color = PixelSetColors.Primary,
                modifier = Modifier.clickable { viewModel.resetAdvancedEdits() },
            )
        }
        PixelSetText("旋转", fontSize = 12.sp, color = PixelSetColors.TextMuted)
        SegmentedControl(
            options = RotationStep.entries.map { it.label },
            selectedIndex = viewModel.rotationIndex,
            onSelected = viewModel::updateRotation,
        )
        PixelSetText("预览区内拖拽白框移动裁剪区域；亦可调节边距滑块", fontSize = 12.sp, color = PixelSetColors.TextMuted)
        PixelSetText("裁剪边距（占原图比例）", fontSize = 12.sp, color = PixelSetColors.TextMuted)
        ParamSlider("左侧", viewModel.cropLeft, "${(viewModel.cropLeft * 100).toInt()}%", viewModel::updateCropLeft, 0f..0.45f)
        ParamSlider("上侧", viewModel.cropTop, "${(viewModel.cropTop * 100).toInt()}%", viewModel::updateCropTop, 0f..0.45f)
        ParamSlider("右侧", viewModel.cropRight, "${(viewModel.cropRight * 100).toInt()}%", viewModel::updateCropRight, 0f..0.45f)
        ParamSlider("下侧", viewModel.cropBottom, "${(viewModel.cropBottom * 100).toInt()}%", viewModel::updateCropBottom, 0f..0.45f)
        PixelSetText("色彩", fontSize = 12.sp, color = PixelSetColors.TextMuted)
        ParamSlider(
            "亮度",
            viewModel.brightness.toFloat(),
            "${viewModel.brightness}",
            { viewModel.updateBrightness(it) },
            -100f..100f,
        )
        ParamSlider(
            "对比度",
            viewModel.contrast,
            "${(viewModel.contrast * 100).toInt()}%",
            viewModel::updateContrast,
            0.5f..2f,
        )
        ParamSlider(
            "饱和度",
            viewModel.saturation,
            "${(viewModel.saturation * 100).toInt()}%",
            viewModel::updateSaturation,
            0f..2f,
        )
    }
}

@Composable
private fun AutoPreviewToggle(
    enabled: Boolean,
    onEnabledChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f).padding(end = 12.dp)) {
            PixelSetText(
                "自动预览",
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = PixelSetColors.TextPrimary,
            )
            PixelSetText(
                "修改参数后按每张图的独立设置自动预览全部图片",
                fontSize = 12.sp,
                color = PixelSetColors.TextMuted,
            )
        }
        Switch(
            checked = enabled,
            onCheckedChange = onEnabledChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.White,
                checkedTrackColor = PixelSetColors.Primary,
                uncheckedThumbColor = Color.White,
                uncheckedTrackColor = PixelSetColors.Border,
            ),
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ActionButtons(viewModel: PixelSetViewModel, sizeClass: WindowSizeClass) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        val statusText = when {
            viewModel.batchProgress.isNotEmpty() -> viewModel.batchProgress
            viewModel.autoPreviewProgress.isNotEmpty() -> viewModel.autoPreviewProgress
            else -> ""
        }
        if (statusText.isNotEmpty()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PixelSetText(statusText, fontSize = 12.sp, color = PixelSetColors.Primary)
                if (viewModel.isBatchCancellable) {
                    PixelSetText(
                        "取消",
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.error,
                        fontWeight = FontWeight.Medium,
                        modifier = Modifier.clickable { viewModel.cancelProcessing() },
                    )
                }
            }
        }
        val buttonShape = RoundedCornerShape(8.dp)
        val showBatch = viewModel.images.size > 1

        if (sizeClass.isCompact()) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                if (viewModel.isBatchCancellable) {
                    OutlinedButton(
                        onClick = { viewModel.cancelProcessing() },
                        shape = buttonShape,
                        modifier = Modifier.fillMaxWidth(),
                    ) { PixelSetText("取消处理") }
                }
                if (showBatch) {
                    OutlinedButton(
                        onClick = { viewModel.processAllImages() },
                        enabled = viewModel.hasImages && !viewModel.isProcessing,
                        shape = buttonShape,
                        modifier = Modifier.fillMaxWidth(),
                    ) { PixelSetText("处理全部") }
                    OutlinedButton(
                        onClick = { viewModel.downloadAllProcessed() },
                        enabled = viewModel.processedCount > 0 && !viewModel.isProcessing,
                        shape = buttonShape,
                        modifier = Modifier.fillMaxWidth(),
                    ) { PixelSetText(batchDownloadLabel(viewModel.processedCount)) }
                }
                Button(
                    onClick = { viewModel.processImage() },
                    enabled = viewModel.originalBitmap != null && !viewModel.isProcessing,
                    shape = buttonShape,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = PixelSetColors.Primary),
                ) {
                    ProcessButtonContent(viewModel)
                }
                OutlinedButton(
                    onClick = { viewModel.downloadProcessed() },
                    enabled = viewModel.processedResult != null && !viewModel.isProcessing,
                    shape = buttonShape,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Icon(Icons.Default.Download, null, modifier = Modifier.size(18.dp).padding(end = 4.dp))
                    PixelSetText("下载")
                }
            }
        } else {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                if (viewModel.isBatchCancellable) {
                    OutlinedButton(
                        onClick = { viewModel.cancelProcessing() },
                        shape = buttonShape,
                    ) { PixelSetText("取消处理") }
                }
                if (showBatch) {
                    OutlinedButton(
                        onClick = { viewModel.downloadAllProcessed() },
                        enabled = viewModel.processedCount > 0 && !viewModel.isProcessing,
                        shape = buttonShape,
                    ) { PixelSetText(batchDownloadLabel(viewModel.processedCount)) }
                    OutlinedButton(
                        onClick = { viewModel.processAllImages() },
                        enabled = viewModel.hasImages && !viewModel.isProcessing,
                        shape = buttonShape,
                    ) { PixelSetText("处理全部") }
                }
                OutlinedButton(
                    onClick = { viewModel.downloadProcessed() },
                    enabled = viewModel.processedResult != null && !viewModel.isProcessing,
                    shape = buttonShape,
                ) {
                    Icon(Icons.Default.Download, null, modifier = Modifier.size(18.dp).padding(end = 4.dp))
                    PixelSetText("下载")
                }
                Button(
                    onClick = { viewModel.processImage() },
                    enabled = viewModel.originalBitmap != null && !viewModel.isProcessing,
                    shape = buttonShape,
                    colors = ButtonDefaults.buttonColors(containerColor = PixelSetColors.Primary),
                ) {
                    ProcessButtonContent(viewModel)
                }
            }
        }
    }
}

@Composable
private fun batchDownloadLabel(processedCount: Int): String =
    if (processedCount > 1) "下载 ZIP ($processedCount)" else "下载 ($processedCount)"

@Composable
private fun ProcessButtonContent(viewModel: PixelSetViewModel) {
    if (viewModel.isProcessing && viewModel.batchProgress.isEmpty()) {
        CircularProgressIndicator(
            modifier = Modifier.size(18.dp).padding(end = 8.dp),
            color = Color.White,
            strokeWidth = 2.dp,
        )
    } else {
        Icon(Icons.Default.PlayArrow, null, modifier = Modifier.size(18.dp).padding(end = 4.dp))
    }
    PixelSetText(if (viewModel.images.size > 1) "处理当前" else "立即处理")
}

private const val SOURCE_CODE_URL = "https://gitee.com/hzh_admin/ledger-flow-master"

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun PixelSetFooter(sizeClass: WindowSizeClass) {
    var activeDialog by remember { mutableStateOf<FooterDialog?>(null) }
    val padding = sizeClass.horizontalPadding()

    activeDialog?.let { dialog ->
        FooterInfoDialog(dialog = dialog, onDismiss = { activeDialog = null })
    }

    Column {
        HorizontalDivider(color = PixelSetColors.Border)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(PixelSetColors.Surface)
                .padding(horizontal = padding, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Row(
                verticalAlignment = Alignment.Top,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Icon(
                    Icons.Default.Security,
                    null,
                    tint = PixelSetColors.Primary,
                    modifier = Modifier.size(14.dp).padding(top = 2.dp),
                )
                PixelSetText(
                    "处理后图片不会上传到服务器，请放心使用",
                    fontSize = 12.sp,
                    color = PixelSetColors.TextSecondary,
                    lineHeight = 18.sp,
                )
            }
            if (sizeClass.isCompact()) {
                PixelSetText(
                    "© 2024 PixelSet · 所有图片均在本地处理",
                    fontSize = 11.sp,
                    color = PixelSetColors.TextMuted,
                )
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    FooterLink("格式支持") { activeDialog = FooterDialog.FormatSupport }
                    FooterLinkDot()
                    FooterLink("FAQ") { activeDialog = FooterDialog.Faq }
                    FooterLinkDot()
                    FooterLink("隐私政策") { activeDialog = FooterDialog.PrivacyPolicy }
                    FooterLinkDot()
                    FooterLink("源代码") { openExternalUrl(SOURCE_CODE_URL) }
                }
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    PixelSetText(
                        "© 2024 PixelSet · 所有图片均在本地处理",
                        fontSize = 11.sp,
                        color = PixelSetColors.TextMuted,
                    )
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        FooterLink("格式支持") { activeDialog = FooterDialog.FormatSupport }
                        FooterLinkDot()
                        FooterLink("FAQ") { activeDialog = FooterDialog.Faq }
                        FooterLinkDot()
                        FooterLink("隐私政策") { activeDialog = FooterDialog.PrivacyPolicy }
                        FooterLinkDot()
                        FooterLink("源代码") { openExternalUrl(SOURCE_CODE_URL) }
                    }
                }
            }
        }
    }
}

@Composable
private fun FooterLink(text: String, onClick: () -> Unit) {
    val interactionSource = remember { MutableInteractionSource() }
    val isHovered by interactionSource.collectIsHoveredAsState()
    PixelSetText(
        text = text,
        fontSize = 11.sp,
        color = if (isHovered) PixelSetColors.Primary else PixelSetColors.TextSecondary,
        fontWeight = if (isHovered) FontWeight.Medium else FontWeight.Normal,
        modifier = Modifier
            .hoverable(interactionSource)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 2.dp, vertical = 2.dp),
    )
}

@Composable
private fun FooterLinkDot() {
    PixelSetText("·", fontSize = 11.sp, color = PixelSetColors.TextMuted)
}

